const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const ORDER_PHOTOS_BUCKET = "order-photos";

// Predictable public URL for an object path in the order-photos bucket
// (bucket is public - see order_upload_storage_migration memory). FALLBACK
// ONLY: the authoritative publicUrl now comes back from the server inside
// each upload token, because building it here once produced
// "undefined/storage/..." srcs when REACT_APP_SUPABASE_URL wasn't set in the
// frontend's env - which shipped PhotoBooks with dozens of blank pages
// (order #909, 2026-07-28). Returns null instead of a garbage string when
// the env var is missing so callers can treat the file as failed.
export function publicUrlForPath(path) {
  if (!SUPABASE_URL || !/^https?:\/\//i.test(SUPABASE_URL)) return null;
  return `${SUPABASE_URL}/storage/v1/object/public/${ORDER_PHOTOS_BUCKET}/${path}`;
}

// Runs `worker(item)` over all items with at most `limit` in flight at once.
// Uploading 3 photos concurrently instead of strictly one-by-one roughly
// cuts total upload wait to a third on a normal connection, while staying
// gentle enough for memory-constrained in-app browsers (Instagram/Facebook).
export async function runWithConcurrency(items, limit, worker) {
  let next = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const idx = next++;
      await worker(items[idx], idx);
    }
  });
  await Promise.all(runners);
}

export const UPLOAD_CONCURRENCY = 3;

// Safari reports fetch network failures as "TypeError: Load failed" (no
// "fetch" substring), while Chrome says "Failed to fetch" - both must be
// treated as retryable, or every blip forces the customer to redo the upload.
const isRetryableError = (err) =>
  err.name === "AbortError" ||
  (err.name === "TypeError" && /fetch|load failed|network/i.test(err.message || "")) ||
  (err.status && err.status >= 500);

// PUTs a single file directly to a Supabase Storage signed upload URL, with
// retry - this replaces routing the raw photo bytes through our own
// Express/multer server. Moving the transfer to Storage's own infrastructure
// (instead of a single VPS) is what actually fixes most of the "grupi X
// dështoi" failures - see order_upload_storage_migration memory. This is a
// plain signed-URL PUT rather than true TUS/resumable: the TUS endpoint's
// x-signature auth couldn't be gotten to verify against the live project
// despite matching the documented header shape (tested directly with curl),
// while this plain signed-URL PUT worked immediately. For the modest
// (sub-few-MB, already-compressed) photo sizes here, whole-file retry is an
// acceptable trade for using a confirmed-working path instead of guessing at
// undocumented TUS auth details in production.
export async function uploadToSignedUrl(signedUrl, file, { maxAttempts = 5, timeoutMs = 120000 } = {}) {
  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(signedUrl, {
        method: "PUT",
        headers: {
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": file.type || "application/octet-stream",
          "x-upsert": "true",
        },
        body: file,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        const err = new Error(`Storage upload failed ${response.status}: ${errorText}`);
        err.status = response.status;
        throw err;
      }
      return true;
    } catch (err) {
      clearTimeout(timeoutId);
      lastErr = err;
      if (!isRetryableError(err) || attempt === maxAttempts) throw err;
      // Longer backoff than a single blip needs - the failures this recovers
      // from are often a WiFi/cellular handoff or an in-app browser
      // (Instagram/Facebook) reclaiming memory mid-transfer, both of which
      // take a few seconds to settle, not milliseconds.
      await new Promise((resolve) => setTimeout(resolve, attempt * 2000));
    }
  }
  throw lastErr;
}

// Requests one signed upload URL per filename from our server (which holds
// the service_role credentials needed to mint them - the browser never
// touches those, only the resulting one-time, path-scoped URLs).
export async function getPhotoUploadTokens(apiUrl, orderId, fileNames) {
  const response = await fetch(`${apiUrl}/upload-order/photo-tokens`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ orderId, fileNames }),
  });
  if (!response.ok) {
    throw new Error(`Gabim gjatë përgatitjes së ngarkimit (${response.status}).`);
  }
  const data = await response.json();
  if (!data.success) throw new Error("Gabim gjatë përgatitjes së ngarkimit.");
  return data.tokens || [];
}

// Tells our server which Storage paths were actually uploaded successfully,
// so it can record them in order_details and return their public URLs.
export async function confirmUploadedPhotos(apiUrl, orderId, supabaseOrderId, paths) {
  if (paths.length === 0) return [];
  const response = await fetch(`${apiUrl}/upload-order/confirm-photos`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ orderId, supabaseOrderId, paths }),
  });
  if (!response.ok) {
    throw new Error(`Gabim gjatë ruajtjes së fotove (${response.status}).`);
  }
  const data = await response.json();
  if (!data.success) throw new Error("Gabim gjatë ruajtjes së fotove.");
  return data.photoUrls || [];
}

// Uploads many files to Storage in batches (one token request per batch,
// instead of one per file) and returns a Map of fileName -> public URL for
// whichever ones succeeded. A fileName missing from the returned Map means
// it failed (either no token was issued, or the upload itself failed after
// retries) - callers decide how strict to be about that (PhotoBook page
// slots are print-critical and should block the order; the reference-copy
// gallery elsewhere tolerates gaps and just tells the customer about them).
export async function uploadFilesToStorage(apiUrl, orderId, files, { chunkSize = 12, onProgress } = {}) {
  const results = new Map();
  let done = 0;

  for (let i = 0; i < files.length; i += chunkSize) {
    const chunk = files.slice(i, i + chunkSize);
    const fileNames = chunk.map((f) => f.fileName);

    let tokens = [];
    try {
      tokens = await getPhotoUploadTokens(apiUrl, orderId, fileNames);
    } catch (err) {
      console.error("Failed to get upload tokens for batch:", err);
    }
    const tokenByFileName = new Map(tokens.map((t) => [t.fileName, t]));

    await runWithConcurrency(chunk, UPLOAD_CONCURRENCY, async ({ fileName, blob }) => {
      const tokenInfo = tokenByFileName.get(fileName);
      if (tokenInfo) {
        try {
          await uploadToSignedUrl(tokenInfo.signedUrl, blob);
          // Server-computed publicUrl is authoritative; the client-built one
          // is only a fallback for a server that predates the publicUrl field.
          const url = tokenInfo.publicUrl || publicUrlForPath(tokenInfo.path);
          if (url && /^https?:\/\//i.test(url)) {
            results.set(fileName, url);
          } else {
            // Never record a garbage URL - a missing entry marks the file as
            // failed, which callers surface instead of printing a blank page.
            console.error(`No valid public URL available for ${fileName} - treating upload as failed`);
          }
        } catch (err) {
          console.error(`Storage upload failed for ${fileName} after retries:`, err);
        }
      }
      done++;
      if (onProgress) onProgress(done, files.length);
    });
  }

  return results;
}
